'use client';
import React, { useState, useEffect, useRef } from 'react';

import IconMinus from '@/fo/icons/base/icons/minus.svg';
import IconPlus from '@/fo/icons/base/icons/plus.svg';
import { UiKitLoaderBase } from '@/fo/ui/kit/loader/base';

import { styles } from './UiProductCounterInputBase.styles';

export interface UiProductCounterInputBaseProps {
  className?: string;
  value?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
  onDebounceChange?: (value: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export function UiProductCounterInputBase({
  className,
  value = 1,
  min = 0,
  max = Infinity,
  onChange,
  onDebounceChange,
  onFocus,
  onBlur,
  disabled = false,
  loading = false
}: UiProductCounterInputBaseProps) {
  const initialValue = value > max ? max : value;
  const [currentValue, setCurrentValue] = useState(initialValue);
  const [inputValue, setInputValue] = useState(String(initialValue));
  const slots = styles({ loading, isMaxReached: currentValue >= max });

  const refTimerDebounceChange = useRef(-1);
  const refTimerValidation = useRef(-1);

  useEffect(() => {
    return () => {
      clearTimeout(refTimerDebounceChange.current);
      clearTimeout(refTimerValidation.current);
    };
  }, []);

  useEffect(() => {
    const newValue = value > max ? max : value;
    setCurrentValue(newValue);
    setInputValue(String(newValue));
  }, [value, max]);

  const handleChange = (value: number) => {
    if (onChange) {
      onChange(value);
    }

    if (onDebounceChange) {
      clearTimeout(refTimerDebounceChange.current);
      refTimerDebounceChange.current = window.setTimeout(() => {
        onDebounceChange(value);
      }, 300);
    }
  };

  const validateAndApplyValue = (newValue: number) => {
    const validValue = Math.max(min, Math.min(max, newValue));
    setCurrentValue(validValue);
    setInputValue(String(validValue));
    handleChange(validValue);
  };

  const onDecrease = () => {
    if (currentValue > min) {
      const newValue = currentValue - 1;
      validateAndApplyValue(newValue);
    }
  };

  const onIncrease = () => {
    if (currentValue < max) {
      const newValue = currentValue + 1;
      validateAndApplyValue(newValue);
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = event.target.value;

    // Разрешаем только цифры и пустую строку
    if (inputText !== '' && !/^\d+$/.test(inputText)) {
      return;
    }

    setInputValue(inputText);

    clearTimeout(refTimerValidation.current);

    if (inputText === '') {
      return;
    }

    const newValue = Number(inputText);

    if (!isNaN(newValue) && newValue > 0) {
      if (newValue > max) {
        validateAndApplyValue(max);
      } else if (newValue < min) {
        validateAndApplyValue(min);
      } else {
        refTimerValidation.current = window.setTimeout(() => {
          setCurrentValue(newValue);
          handleChange(newValue);
        }, 500);
      }
    }
  };

  const onInputBlur = () => {
    clearTimeout(refTimerValidation.current);

    const newValue = Number(inputValue);

    if (inputValue === '' || isNaN(newValue)) {
      setInputValue(String(currentValue));
    } else {
      validateAndApplyValue(newValue);
    }

    if (onBlur) {
      onBlur();
    }
  };

  return (
    <div className={slots.base({ className })}>
      <UiKitLoaderBase show={loading} />
      <button
        onClick={onDecrease}
        className={slots.button()}
        disabled={currentValue <= min || disabled || loading}
      >
        <IconMinus />
      </button>
      <input
        className={slots.input()}
        inputMode="numeric"
        pattern="\d*"
        value={inputValue}
        onChange={onInputChange}
        onFocus={onFocus}
        onBlur={onInputBlur}
        disabled={disabled || loading}
      />
      <button
        className={slots.buttonPlus({ isMaxReached: currentValue >= max })}
        onClick={onIncrease}
        disabled={currentValue >= max || disabled || loading}
      >
        <IconPlus />
      </button>
    </div>
  );
}
